import { apiRequest } from '@/utils/api-request';

import {
  GoogleConnectionsResponse,
  GoogleDisconnectResult,
  GoogleInitiateResult,
  GoogleService,
} from './types';

export class GoogleConnectionsError extends Error {
  constructor(
    message: string,
    readonly statusCode?: number,
  ) {
    super(message);
    this.name = 'GoogleConnectionsError';
  }
}

const base = (orgId: string) => `/api/v1/orgs/${orgId}/google-connections`;

const unwrap = <T>(
  response: { data?: T; detail?: string; statusCode?: number } | undefined,
  fallback: string,
): T => {
  if (response?.detail || !response?.data) {
    throw new GoogleConnectionsError(
      response?.detail || fallback,
      response?.statusCode,
    );
  }
  return response.data;
};

export const listGoogleConnections = async (
  orgId: string,
): Promise<GoogleConnectionsResponse> =>
  unwrap(
    await apiRequest<GoogleConnectionsResponse>({
      url: base(orgId),
      method: 'GET',
      target: 'workflow',
    }),
    'Could not load Google connection status',
  );

export const initiateGoogleConnection = async (
  orgId: string,
  service: GoogleService,
): Promise<GoogleInitiateResult> =>
  unwrap(
    await apiRequest<GoogleInitiateResult>({
      url: `${base(orgId)}/${service}/initiate`,
      method: 'POST',
      target: 'workflow',
      body: { popup: true },
    }),
    'Could not start Google authorization',
  );

export const disconnectGoogleConnection = async (
  orgId: string,
  service: GoogleService,
  pendingOnly = false,
): Promise<GoogleDisconnectResult> =>
  unwrap(
    await apiRequest<GoogleDisconnectResult>({
      url: `${base(orgId)}/${service}${pendingOnly ? '?pending_only=true' : ''}`,
      method: 'DELETE',
      target: 'workflow',
    }),
    'Could not disconnect the account',
  );
